export type IncludeExclude = 'include' | 'exclude'
export type AnyAll = 'any' | 'all'
export type RoundTiming = 'now' | 'soon' | 'any'
// Tags isn't sortable: an opportunity's tags have no order to sort by.
export const SORT_COLUMNS = [
  'funder',
  'opportunity',
  'link',
  'application_type',
  'location',
  'timing',
] as const
export type SearchSortColumn = (typeof SORT_COLUMNS)[number]

export interface DatabaseSearchFilters {
  region_ids: number[]
  international: IncludeExclude
  gambling: IncludeExclude
  relational: IncludeExclude
  iwi_eligible: boolean
  iwi_ids: number[]
  min_amount: number | null
  timing: RoundTiming
  generalist: IncludeExclude
  keywords: string[]
  keyword_mode: AnyAll
  include_tags: string[]
  include_tag_mode: AnyAll
  exclude_tags: string[]
}

// The server refuses requests past these (MAX_KEYWORDS, MAX_KEYWORD_LENGTH in
// database_search_models.py), so the keyword box stops at them first.
export const MAX_KEYWORDS = 20
export const MAX_KEYWORD_LENGTH = 100

export const DEFAULT_SEARCH_FILTERS: DatabaseSearchFilters = {
  region_ids: [],
  international: 'include',
  gambling: 'include',
  relational: 'include',
  iwi_eligible: false,
  iwi_ids: [],
  min_amount: null,
  timing: 'soon',
  generalist: 'include',
  keywords: [],
  keyword_mode: 'any',
  include_tags: [],
  include_tag_mode: 'any',
  exclude_tags: [],
}

export interface DatabaseSearchRequest {
  filters: DatabaseSearchFilters
  page: number
  sort_by: SearchSortColumn
  sort_dir: 'asc' | 'desc'
}

export interface DatabaseSearchResult {
  /** Not displayed; keys rows and selections. */
  opportunity_uuid: string
  funder: string
  opportunity: string
  link: string | null
  link_is_funder_website: boolean
  application_type: 'Contestable' | 'Relational'
  location: 'International' | 'National' | 'Local'
  locations: string[]
  timing: string
  closing_soon: boolean
  tags: string[]
}

export interface DatabaseSearchPage {
  items: DatabaseSearchResult[]
  total: number
  page: number
  page_size: number
}

// Matches the keyword box, which treats case and accents as the same keyword.
function foldKeyword(text: string) {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

/**
 * A stable fingerprint of the filters. Two states with the same fingerprint
 * search the same thing, so reverting an edit clears the "changed" warning.
 */
export function filtersSignature(filters: DatabaseSearchFilters): string {
  const sorted = (values: (number | string)[]) =>
    [...values].sort((a, b) => String(a).localeCompare(String(b)))
  return JSON.stringify({
    ...filters,
    region_ids: sorted(filters.region_ids),
    iwi_ids: sorted(filters.iwi_eligible ? filters.iwi_ids : []),
    keywords: sorted(filters.keywords.map(foldKeyword)),
    // An any/all switch with nothing to combine changes nothing.
    keyword_mode: filters.keywords.length ? filters.keyword_mode : DEFAULT_SEARCH_FILTERS.keyword_mode,
    include_tags: sorted(filters.include_tags),
    include_tag_mode: filters.include_tags.length
      ? filters.include_tag_mode
      : DEFAULT_SEARCH_FILTERS.include_tag_mode,
    exclude_tags: sorted(filters.exclude_tags),
  })
}

/** True when any filter differs from the defaults; the any/all switches alone do not count. */
export function hasAnyFilter(filters: DatabaseSearchFilters): boolean {
  const withDefaultModes = {
    ...filters,
    keyword_mode: DEFAULT_SEARCH_FILTERS.keyword_mode,
    include_tag_mode: DEFAULT_SEARCH_FILTERS.include_tag_mode,
  }
  return filtersSignature(withDefaultModes) !== filtersSignature(DEFAULT_SEARCH_FILTERS)
}
